(function () {
  "use strict";

  var CONFIG = window.YE_POPUP_CONFIG;
  if (!CONFIG) {
    console.error("[YE Popup] window.YE_POPUP_CONFIG is missing — config.js must load before popup.js");
    return;
  }

  var STORAGE_KEY = "ye_popup_state_v2";
  var VISITED_BEFORE_KEY = "ye_visited_before";
  var SESSION_MARKED_KEY = "ye_session_marked";
  var SESSION_IS_NEW_KEY = "ye_session_is_new";
  var KLAVIYO_REVISION = "2026-07-15";

  var overlayEl = null;
  var modalOpen = false;
  var scrollHandlerAttached = false;
  var exitIntentAttached = false;
  var lastFocusedEl = null;

  function getState() {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function setState(patch) {
    var state = getState();
    for (var key in patch) {
      if (Object.prototype.hasOwnProperty.call(patch, key)) {
        state[key] = patch[key];
      }
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      // storage unavailable (private mode etc.) — degrade to session-only behavior
    }
    return state;
  }

  // Popup events also go to Klaviyo (onsite JS is already on the storefront) so
  // impressions/dismissals/conversions can be reported next to list signups.
  var KLAVIYO_EVENT_NAMES = {
    ye_popup_impression: "YE Popup Impression",
    ye_popup_dismiss: "YE Popup Dismissed",
    ye_popup_conversion: "YE Popup Converted"
  };

  function trackKlaviyo(eventName, detail) {
    var klaviyoName = KLAVIYO_EVENT_NAMES[eventName];
    if (!klaviyoName) return;
    var props = {
      device: window.innerWidth < 768 ? "mobile" : "desktop",
      page_path: window.location.pathname
    };
    var keys = Object.keys(detail || {});
    for (var i = 0; i < keys.length; i++) {
      // never send the email address (or its domain) as an event property
      if (keys[i] !== "email_domain") props[keys[i]] = detail[keys[i]];
    }
    try {
      var queue = window.klaviyo || (window._learnq = window._learnq || []);
      queue.push(["track", klaviyoName, props]);
    } catch (e) {
      // analytics must never break the popup
    }
  }

  function track(eventName, detail) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, detail || {}));
    trackKlaviyo(eventName, detail);
  }

  function isExcludedPath() {
    var path = window.location.pathname;
    var excludes = (CONFIG.targeting && CONFIG.targeting.excludePaths) || [];
    for (var i = 0; i < excludes.length; i++) {
      if (path.indexOf(excludes[i]) === 0) return true;
    }
    return false;
  }

  // A "new visitor" is someone in their first-ever browsing session on this
  // browser. The classification is decided once per session (so it stays
  // consistent across multiple pages within one visit) and persisted in
  // sessionStorage; a separate localStorage flag remembers "seen before" for
  // all future sessions.
  function isNewVisitorSession() {
    try {
      if (window.sessionStorage.getItem(SESSION_MARKED_KEY) !== "true") {
        var visitedBefore = window.localStorage.getItem(VISITED_BEFORE_KEY) === "true";
        window.sessionStorage.setItem(SESSION_IS_NEW_KEY, visitedBefore ? "false" : "true");
        window.sessionStorage.setItem(SESSION_MARKED_KEY, "true");
        window.localStorage.setItem(VISITED_BEFORE_KEY, "true");
      }
      return window.sessionStorage.getItem(SESSION_IS_NEW_KEY) === "true";
    } catch (e) {
      return true; // storage unavailable — fail open rather than silently never showing
    }
  }

  // Only detects past purchases for logged-in customers — set server-side by
  // snippet.liquid from {{ customer.orders_count }}. Anonymous/guest-checkout
  // returning customers can't be identified without a backend.
  function isPastCustomer() {
    return window.YE_POPUP_HAS_ORDERED === true;
  }

  function shouldShow() {
    if (modalOpen) return false;
    if (isExcludedPath()) return false;
    if (CONFIG.targeting.excludePastCustomers && isPastCustomer()) return false;
    if (CONFIG.targeting.newVisitorsOnly && !isNewVisitorSession()) return false;

    var state = getState();
    if (state.converted) return false;

    var maxImpressions = CONFIG.frequency.maxImpressions || 2;
    var impressionCount = state.impressionCount || 0;
    if (impressionCount >= maxImpressions) return false;

    if (impressionCount >= 1 && state.lastImpressionAt) {
      var minGapMs = (CONFIG.frequency.minHoursBetweenImpressions || 1) * 60 * 60 * 1000;
      if (Date.now() - state.lastImpressionAt < minGapMs) return false;
    }

    return true;
  }

  function isMobile() {
    return window.matchMedia && window.matchMedia("(max-width: 768px)").matches;
  }

  function buildHeroImagesHtml() {
    var images = CONFIG.productImages || [];
    return images
      .map(function (img) {
        return '<img class="ye-popup-hero-img" src="' + img.url + '" alt="' + img.alt + '" loading="lazy" />';
      })
      .join("");
  }

  function buildConfettiHtml() {
    var html = "";
    for (var i = 0; i < 8; i++) {
      html += '<span class="ye-confetti-piece"></span>';
    }
    return html;
  }

  function buildModal() {
    if (overlayEl) return overlayEl;

    var c = CONFIG.copy;

    var overlay = document.createElement("div");
    overlay.className = "ye-popup-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-label", c.heading);

    overlay.innerHTML =
      '<div class="ye-popup-wrap">' +
      '<div class="ye-popup-confetti">' + buildConfettiHtml() + "</div>" +
      '<div class="ye-popup-hero">' + buildHeroImagesHtml() + "</div>" +
      '<div class="ye-popup-box">' +
      '<button type="button" class="ye-popup-close" aria-label="Close">&times;</button>' +
      '<div class="ye-popup-content">' +
      '<div class="ye-popup-form-wrap">' +
      '<h2 class="ye-popup-heading">' + c.heading + "</h2>" +
      '<p class="ye-popup-subheading">' + c.subheading + "</p>" +
      '<p class="ye-popup-body">' + c.body + "</p>" +
      '<form class="ye-popup-form" novalidate>' +
      '<input type="email" class="ye-popup-input" placeholder="' + c.emailPlaceholder + '" required autocomplete="email" />' +
      '<p class="ye-popup-error" aria-live="polite"></p>' +
      '<button type="submit" class="ye-popup-submit">' + c.submitButton + "</button>" +
      '<button type="button" class="ye-popup-no-thanks">' + c.noThanksButton + "</button>" +
      "</form>" +
      "</div>" +
      '<div class="ye-popup-success">' +
      '<h2 class="ye-popup-heading">' + c.successHeading + "</h2>" +
      '<p class="ye-popup-subheading">' + c.successSubheading + "</p>" +
      '<p class="ye-popup-body">' + c.successMessage + "</p>" +
      '<p class="ye-popup-body">' + c.successMessage2 + "</p>" +
      "</div>" +
      "</div>" +
      "</div>" +
      "</div>";

    document.body.appendChild(overlay);
    overlayEl = overlay;

    overlay.querySelector(".ye-popup-close").addEventListener("click", function () {
      closeModal("close_button");
    });

    overlay.querySelector(".ye-popup-no-thanks").addEventListener("click", function () {
      closeModal("no_thanks");
    });

    overlay.addEventListener("click", function (e) {
      if (e.target === overlay) closeModal("overlay_click");
    });

    overlay.querySelector("form").addEventListener("submit", function (e) {
      e.preventDefault();
      handleSubmit(overlay);
    });

    document.addEventListener("keydown", function (e) {
      if (!modalOpen) return;
      if (e.key === "Escape") closeModal("escape_key");
      if (e.key === "Tab") trapFocus(e, overlay);
    });

    return overlay;
  }

  function trapFocus(e, overlay) {
    var focusable = overlay.querySelectorAll("button, input, [href]");
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function openModal(triggerName) {
    if (modalOpen) return;
    var overlay = buildModal();
    lastFocusedEl = document.activeElement;
    modalOpen = true;
    overlay.classList.remove("ye-popup-success-state");
    overlay.classList.add("ye-popup-open");
    document.body.style.overflow = "hidden";

    var state = getState();
    setState({
      impressionCount: (state.impressionCount || 0) + 1,
      lastImpressionAt: Date.now()
    });
    track("ye_popup_impression", { trigger: triggerName });

    setTimeout(function () {
      var input = overlay.querySelector(".ye-popup-input");
      if (input) input.focus();
    }, 50);
  }

  function closeModal(reason) {
    if (!overlayEl || !modalOpen) return;
    modalOpen = false;
    overlayEl.classList.remove("ye-popup-open");
    document.body.style.overflow = "";
    track("ye_popup_dismiss", { reason: reason });
    if (lastFocusedEl && lastFocusedEl.focus) lastFocusedEl.focus();
  }

  function validateEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function handleSubmit(overlay) {
    var input = overlay.querySelector(".ye-popup-input");
    var errorEl = overlay.querySelector(".ye-popup-error");
    var submitBtn = overlay.querySelector(".ye-popup-submit");
    var email = (input.value || "").trim();

    errorEl.textContent = "";

    if (!validateEmail(email)) {
      errorEl.textContent = CONFIG.copy.invalidEmailMessage;
      return;
    }

    submitBtn.disabled = true;
    submitBtn.textContent = "...";

    subscribeToKlaviyo(email)
      .then(function () {
        setState({ converted: true });
        track("ye_popup_conversion", { email_domain: email.split("@")[1] });
        overlay.classList.add("ye-popup-success-state");
      })
      .catch(function () {
        errorEl.textContent = CONFIG.copy.errorMessage;
      })
      .finally(function () {
        submitBtn.disabled = false;
        submitBtn.textContent = CONFIG.copy.submitButton;
      });
  }

  function subscribeToKlaviyo(email) {
    var url = "https://a.klaviyo.com/client/subscriptions?company_id=" + CONFIG.klaviyo.companyId;
    var body = {
      data: {
        type: "subscription",
        attributes: {
          custom_source: "Website Popup - Mystery Discount",
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: email,
                subscriptions: {
                  email: {
                    marketing: {
                      consent: "SUBSCRIBED"
                    }
                  }
                }
              }
            }
          }
        },
        relationships: {
          list: {
            data: {
              type: "list",
              id: CONFIG.klaviyo.listId
            }
          }
        }
      }
    };

    return fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/vnd.api+json",
        revision: KLAVIYO_REVISION
      },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok && res.status !== 202 && res.status !== 204) {
        throw new Error("Klaviyo subscribe failed with status " + res.status);
      }
    });
  }

  function onScroll() {
    var scrollTop = window.scrollY || document.documentElement.scrollTop;
    var docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight <= 0) return;
    var percent = (scrollTop / docHeight) * 100;
    if (percent >= (CONFIG.triggers.scrollDepth.percent || 60)) {
      window.removeEventListener("scroll", onScroll);
      scrollHandlerAttached = false;
      if (shouldShow()) openModal("scroll_depth");
    }
  }

  function onMouseOut(e) {
    if (isMobile()) return;
    if (e.clientY > 0 || e.relatedTarget || e.toElement) return;
    document.removeEventListener("mouseout", onMouseOut);
    exitIntentAttached = false;
    if (shouldShow()) openModal("exit_intent");
  }

  function initTriggers() {
    if (!shouldShow()) return;

    var t = CONFIG.triggers;

    if (t.onLoad && t.onLoad.enabled) {
      openModal("on_load");
      return;
    }

    if (t.timeDelay && t.timeDelay.enabled) {
      setTimeout(function () {
        if (shouldShow()) openModal("time_delay");
      }, (t.timeDelay.seconds || 30) * 1000);
    }

    if (t.scrollDepth && t.scrollDepth.enabled && !scrollHandlerAttached) {
      scrollHandlerAttached = true;
      window.addEventListener("scroll", onScroll, { passive: true });
    }

    if (t.exitIntent && t.exitIntent.enabled && !exitIntentAttached && !isMobile()) {
      exitIntentAttached = true;
      document.addEventListener("mouseout", onMouseOut);
    }
  }

  // Small public API for manual triggering (e.g. a footer "Get $5 off" link) and QA.
  window.YEPopup = {
    open: function () {
      openModal("manual");
    },
    close: function () {
      closeModal("manual");
    },
    resetState: function () {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
        window.localStorage.removeItem(VISITED_BEFORE_KEY);
        window.sessionStorage.removeItem(SESSION_MARKED_KEY);
        window.sessionStorage.removeItem(SESSION_IS_NEW_KEY);
      } catch (e) {}
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initTriggers);
  } else {
    initTriggers();
  }
})();
